import React, { useEffect, useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CreatableSelect } from '../../ui';
import { integrationApiService, ShopifyFieldOwner, StoreLayoutSettings } from '../../../services/integrationApi';
import { tokens } from '../../../theme/tokens';
import { resolveTry, TraceRow } from './v2Resolve';
import { GroupLabel, v2CardSx } from './v2Ui';

interface TryProductProps {
  layout: StoreLayoutSettings | undefined;
  slugs: string[];
  taxonomyOn: boolean;
  hasRegistry: boolean;
  nameRuleCategory: boolean;
  categoryDefaults: { defaults: Record<string, string | null>; neutralGid: string } | undefined;
  options: Array<{ gid: string; label: string }>;
  categoryOwner: ShopifyFieldOwner | undefined;
  typeOwner: ShopifyFieldOwner | undefined;
  /** Flag `shopify_unmapped_category_unset`: a stored "Don't set" fallback leaves the category alone. */
  unmappedCategoryUnset?: boolean;
  /**
   * `side`: one column (inputs, result, trace) for the sticky column beside a panel. `wide`: inputs
   * beside the result, for the full-width card under the overrides table.
   */
  variant?: 'side' | 'wide';
  /** What is typed, held by the parent so it survives the tester moving between the side and the wide spot. */
  inputs?: TryInputs;
  onInputsChange?: (inputs: TryInputs) => void;
}

export interface TryInputs {
  name: string;
  sku: string;
  category: string;
}

export const EMPTY_TRY_INPUTS: TryInputs = { name: '', sku: '', category: '' };

const SKU_DEBOUNCE_MS = 250;

/** Shown number for a trace row: steps renumber when the taxonomy step is not offered. */
const rowNumber = (row: TraceRow, taxonomyOn: boolean): string =>
  row.step === 0 ? '•' : String(taxonomyOn ? row.step : row.step - 1);

/**
 * Try a product: name, store SKU and Motzu category in, both outputs and the step that decided each out.
 * Computed here from the unsaved layout; only the SKU lookup asks the server (the registry matches on it).
 */
const TryProduct: React.FC<TryProductProps> = ({
  layout,
  slugs,
  taxonomyOn,
  hasRegistry,
  nameRuleCategory,
  categoryDefaults,
  options,
  categoryOwner,
  typeOwner,
  unmappedCategoryUnset = false,
  variant = 'wide',
  inputs,
  onInputsChange,
}) => {
  const [ownInputs, setOwnInputs] = useState<TryInputs>(EMPTY_TRY_INPUTS);
  const { name, sku, category } = inputs ?? ownInputs;
  const setInput = (key: keyof TryInputs, value: string) => {
    const next = { ...(inputs ?? ownInputs), [key]: value };
    if (onInputsChange) onInputsChange(next);
    else setOwnInputs(next);
  };
  const setName = (value: string) => setInput('name', value);
  const setSku = (value: string) => setInput('sku', value);
  const setCategory = (value: string) => setInput('category', value);
  const [debouncedSku, setDebouncedSku] = useState('');
  const side = variant === 'side';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSku(sku.trim()), SKU_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [sku]);

  const resolve = useQuery({
    queryKey: ['v2-taxonomy-resolve', debouncedSku],
    queryFn: () => integrationApiService.resolveMerchantTaxonomy({ storeSku: debouncedSku }),
    enabled: taxonomyOn && hasRegistry && debouncedSku.length > 0,
    retry: false,
    staleTime: 30_000,
  });
  const entry = taxonomyOn && hasRegistry && debouncedSku && debouncedSku === sku.trim() ? resolve.data?.entry ?? null : null;

  const active = Boolean(name.trim() || sku.trim() || category.trim());
  const result = useMemo(
    () => resolveTry({
      name,
      sku,
      category,
      layout,
      registryEntry: entry,
      hasRegistry: taxonomyOn && hasRegistry,
      nameRuleCategory,
      categoryDefaults,
      options,
      categoryOwner,
      typeOwner,
      unmappedCategoryUnset,
    }),
    [name, sku, category, layout, entry, taxonomyOn, hasRegistry, nameRuleCategory, categoryDefaults, options, categoryOwner, typeOwner, unmappedCategoryUnset],
  );
  const trace = taxonomyOn ? result.trace : result.trace.filter((row) => row.step !== 1);

  return (
    <Box
      data-testid="try-product"
      data-variant={variant}
      sx={{
        ...v2CardSx,
        px: 2.25,
        py: 2,
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: side ? 'column' : 'row',
        gap: side ? 1.5 : 2.5,
        alignItems: side ? 'stretch' : 'flex-start',
      }}
    >
      <Box sx={{ flex: side ? '0 0 auto' : '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Try a product</Typography>
        <TextField
          size="small"
          fullWidth
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Product name"
          inputProps={{ 'aria-label': 'Product name to try' }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <TextField
            size="small"
            value={sku}
            onChange={(event) => setSku(event.target.value)}
            placeholder="Store SKU"
            inputProps={{ 'aria-label': 'Store SKU to try' }}
            sx={{ flex: '1 1 120px', minWidth: 0 }}
          />
          <Box sx={{ flex: '1 1 120px', minWidth: 0 }}>
            <CreatableSelect
              label=""
              aria-label="Motzu Classification to try"
              value={category}
              options={slugs}
              onChange={setCategory}
              placeholder="Motzu Classification"
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: side ? '0 0 auto' : '1.4 1 360px', minWidth: 0, p: 1.75, borderRadius: '10px', bgcolor: tokens.surface.sunken, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {!active && (
          <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>
            Type a product name, a store SKU or a Motzu Classification to see what the next sync would write and which step decides it.
          </Typography>
        )}
        {active && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: side ? 'minmax(0,1fr)' : 'auto 1fr', columnGap: 1.5, rowGap: 0.5, alignItems: 'baseline' }}>
              <GroupLabel>Shopify Product Type</GroupLabel>
              <Typography component="span" data-testid="try-type" sx={{ fontSize: 14, fontWeight: 700, color: result.typeOk ? tokens.ink.primary : tokens.signal.caution.ink }}>
                {result.type}{' '}
                <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: tokens.ink.muted }}>· {result.typeVia}</Box>
              </Typography>
              <GroupLabel>Shopify Product Category</GroupLabel>
              <Typography component="span" data-testid="try-category" sx={{ fontSize: 14, fontWeight: 700, color: tokens.ink.primary }}>
                {result.category}{' '}
                <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: tokens.ink.muted }}>· {result.categoryVia}</Box>
              </Typography>
            </Box>
            <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {trace.map((row) => (
                <Box
                  component="li"
                  key={row.step}
                  data-testid={`trace-${row.step}`}
                  data-status={row.status}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: side
                      ? '18px minmax(0,1fr)'
                      : { xs: '18px minmax(0,1fr)', md: '18px 190px minmax(0,1fr)' },
                    columnGap: 1,
                    rowGap: '2px',
                    alignItems: 'center',
                    fontSize: 12.5,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      fontSize: 10.5,
                      fontWeight: 700,
                      color: tokens.ink.inverse,
                      bgcolor: row.status === 'decided'
                        ? tokens.signal.info.solid
                        : row.status === 'skipped' ? tokens.signal.neutral.solid : tokens.ink.disabled,
                    }}
                  >
                    {rowNumber(row, taxonomyOn)}
                  </Box>
                  <Box component="span" sx={{ fontWeight: 600, color: row.status === 'decided' ? tokens.ink.primary : tokens.ink.faint }}>{row.label}</Box>
                  <Box
                    component="span"
                    sx={{
                      color: row.status === 'decided' ? tokens.ink.primary : tokens.ink.faint,
                      // One column beside the circle when narrow: the note sits under the step name.
                      gridColumn: side ? '2' : { xs: '2', md: 'auto' },
                    }}
                  >
                    {row.note}
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TryProduct;
